import { useEffect, useState } from 'react'
import { Route, Switch, useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Wrapper from 'components/reusables/wrapper'
import Loader from 'components/ui/loader/Loader'
import { surveyTemplatesService } from 'api/administration/surveyTemplates'
import { useNotification } from 'hooks/notifications/useNotification'
import { PathRoutes } from 'enum/routes'
import { fromSurveyTemplate } from './common/converters'
import type { DraftQuestion } from './common/converters'
import type { SurveyTemplateDraft, SurveyTemplateRow } from './common/types'
import { TRANSLATION_PREFIX } from './constants'
import { SurveyTemplateEditor } from './components/SurveyTemplateEditor'
import { SurveyTemplateList } from './components/SurveyTemplateList'

const emptyDraft: SurveyTemplateDraft = {
  name: '',
  description: '',
  questions: [],
}

const editPath = (id: number) =>
  PathRoutes.SURVEY_BANK_EDIT.replace(':id', String(id))

const EditSurveyTemplate = () => {
  const { t } = useTranslation()
  const notification = useNotification()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const [loaded, setLoaded] = useState<{
    draft: SurveyTemplateDraft
    questions: DraftQuestion[]
  } | null>(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const template = await surveyTemplatesService.show(Number(id))
        if (active) setLoaded(fromSurveyTemplate(template))
      } catch {
        if (!active) return
        notification.error(t(`${TRANSLATION_PREFIX}.errors.load`))
        history.replace(PathRoutes.SURVEY_BANK)
      }
    }
    load()
    return () => {
      active = false
    }
    // Loads once per survey; notification and history are stable helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!loaded) return <Loader color />

  return (
    <SurveyTemplateEditor
      initialDraft={loaded.draft}
      initialQuestions={loaded.questions}
      onDone={() => history.push(PathRoutes.SURVEY_BANK)}
      onCancel={() => history.push(PathRoutes.SURVEY_BANK)}
    />
  )
}

/**
 * Survey bank of the company under Course management: listing, new survey
 * and edition, each with its own URL.
 */
const SurveyBank = () => {
  const history = useHistory()
  const backToList = () => history.push(PathRoutes.SURVEY_BANK)

  return (
    <Wrapper shadow={true}>
      <Switch>
        <Route exact path={PathRoutes.SURVEY_BANK_NEW}>
          <SurveyTemplateEditor
            initialDraft={emptyDraft}
            initialQuestions={[]}
            onDone={backToList}
            onCancel={backToList}
          />
        </Route>
        <Route exact path={PathRoutes.SURVEY_BANK_EDIT}>
          <EditSurveyTemplate />
        </Route>
        <Route>
          <SurveyTemplateList
            onCreate={() => history.push(PathRoutes.SURVEY_BANK_NEW)}
            onEdit={(row: SurveyTemplateRow) => history.push(editPath(row.id))}
          />
        </Route>
      </Switch>
    </Wrapper>
  )
}

export default SurveyBank
